import React, { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import _ from "lodash";
import uuid from "react-uuid";
import Loading from "../../../common/components/loading/loading";
import { getTermSchedules } from "../../../common/services/markets/termScheduleService";
import SearchBox from "../../../common/components/search/searchBox";
import TermSchedulesTable from "./termSchedulesTable";
import { useTranslation } from "react-i18next";

const TermSchedules = ({ onClick, allowEdit }) => {
  const { t } = useTranslation(["markets"]);
  const [termSchedules, setTermSchedules] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [sortColumn, setSortColumn] = useState({ path: "name", order: "asc" });
  const [isLoading, setIsLoading] = useState(true);
  const [paginationState, setPaginationState] = useState({
    currentPage: 0,
    pageSize: 20,
  });

  useEffect(() => {
    (async () => {
      setTermSchedules(await getTermSchedules());
      setIsLoading(false);
    })();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setPaginationState((prev) => ({ ...prev, currentPage: 0 }));
  };

  const handleSort = (colId, sort) => {
    if (colId && sort) {
      setSortColumn({ path: colId, order: sort });
    }
  };

  const handleToggleActiveOnly = () => {
    setActiveOnly((prev) => !prev);
    setPaginationState((prev) => ({ ...prev, currentPage: 0 }));
  };

  const handleTermScheduleSelected = (termSchedule, isNew = false) => {
    onClick(termSchedule, handleTermScheduleUpdated, isNew);
  };

  const handleTermScheduleUpdated = (termSchedule) => {
    setTermSchedules((prev) => {
      const updated = [...prev];
      const index = updated.findIndex(
        (u) => u.termScheduleId === termSchedule.termScheduleId
      );
      if (index > -1) updated.splice(index, 1);
      updated.push(termSchedule);
      return updated;
    });
  };

  const handlePaginationChanged = (params) => {
    if (params && params.api) {
      setPaginationState({
        currentPage: params.api.paginationGetCurrentPage(),
        pageSize: params.api.paginationGetPageSize(),
      });
    }
  };

  const getFilteredData = () => {
    let filtered = termSchedules;
    if (activeOnly) filtered = filtered.filter((u) => u.active);
    if (searchQuery)
      filtered = filtered.filter(
        (u) =>
          (u.name != null &&
            u.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (u.description != null &&
            u.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (u.forwardReportingPeriodicity != null &&
            u.forwardReportingPeriodicity
              .toLowerCase()
              .includes(searchQuery.toLowerCase()))
      );
    const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);
    return { totalCount: filtered.length, data: sorted };
  };

  if (isLoading) return <Loading />;

  const { totalCount, data } = getFilteredData();
  const newTermSchedule = {
    termScheduleId: uuid(),
    name: t("NewTermSchedule"),
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <SearchBox value={searchQuery} onChange={handleSearch} />
        </div>
        <div className="col">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("ActiveOnly")}
            checked={activeOnly}
            value={activeOnly}
            onChange={handleToggleActiveOnly}
          />
        </div>
        <div className="col">
          {allowEdit && (
            <button
              onClick={() => handleTermScheduleSelected(newTermSchedule, true)}
              className="btn btn-sm btn-primary float-sm-right"
            >
              {t("AddTermSchedule")}
            </button>
          )}
        </div>
      </div>
      <p>{t("RetrievedTermSchedule", { count: totalCount })}</p>
      <TermSchedulesTable
        termSchedules={data}
        onSort={handleSort}
        onRowClick={handleTermScheduleSelected}
        onPaginationChanged={handlePaginationChanged}
        paginationState={paginationState}
      />
    </div>
  );
};

export default TermSchedules;
